import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2, CreditCard, Phone, Mail, MapPin, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_7b5007eb-b971-4941-9399-b2d87f9cbac2/artifacts/1lsetayu_barmann%20logo.png";

function useBlobImage(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let active = true;
    let blobUrl;
    axios
      .get(url, { responseType: "blob" })
      .then((r) => {
        if (!active) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  return src;
}

function PhotoTile({ token, photo }) {
  const src = useBlobImage(`${API}/public/photos/${token}/${photo.id}/file`);
  const kindColor =
    photo.kind === "before" ? "text-[#C83E2D] bg-[#FBEAE6]" :
    photo.kind === "after" ? "text-[#1E3B2D] bg-[#E3ECE3]" :
    "text-[#6B7A70] bg-[#EFEDE6]";
  return (
    <figure className="bg-white border border-[#d6d1bf] overflow-hidden">
      <div className="aspect-[4/3] bg-[#eae6db] overflow-hidden">
        {src ? (
          <img src={src} alt={photo.caption || "Job photo"} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#8ba394]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </div>
      <figcaption className="px-3 py-2 flex items-center gap-2">
        <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 ${kindColor}`}>
          {photo.kind}
        </span>
        {photo.caption && <span className="text-xs text-[#0a0f0d]">{photo.caption}</span>}
      </figcaption>
    </figure>
  );
}

export default function CustomerPortal() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(null); // 'checking' | 'paid' | 'timeout' | 'error'
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/invoices/${token}`);
      setInv(data);
    } catch (e) {
      setError(e.response?.status === 404 ? "Invoice not found." : "Could not load invoice.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Returning from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const cancelled = searchParams.get("cancelled");
    if (cancelled) {
      toast.error("Payment was cancelled.");
      const next = new URLSearchParams(searchParams);
      next.delete("cancelled");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!sessionId || pollRef.current) return;
    pollRef.current = true;
    setPolling("checking");

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/invoices/${token}/payment-status/${sessionId}`);
        if (data.payment_status === "paid" || data.invoice_paid) {
          setPolling("paid");
          toast.success("Payment successful — thank you!");
          const next = new URLSearchParams(searchParams);
          next.delete("session_id");
          setSearchParams(next, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          setPolling("timeout");
          toast.error("Checkout session expired.");
          return;
        }
        if (attempts >= 5) {
          setPolling("timeout");
          if (data.payment_status !== "unknown") {
            toast.error("Still processing. Please check back in a minute.");
          }
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setPolling("error");
        toast.error("Could not verify payment.");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/public/invoices/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#141c18] text-[#8ba394]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoice…
      </div>
    );
  }
  if (error || !inv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#141c18] text-[#eae6db] px-6">
        <div className="text-center">
          <div className="font-display text-3xl font-black mb-3">{error || "Invoice unavailable"}</div>
          <div className="text-[#8ba394] text-sm">If you were sent a link, please double-check it or contact us at (678) 559-6163.</div>
        </div>
      </div>
    );
  }

  const paid = inv.status === "paid";
  const photosByJob = inv.photos.reduce((acc, p) => {
    const key = `${p.job_date}__${p.service_type}`;
    if (!acc[key]) acc[key] = { label: p.job_date, service: p.service_type, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});
  const jobKeys = Object.keys(photosByJob);

  return (
    <div className="min-h-screen bg-[#0a0f0d] pb-20" data-testid="customer-portal">
      {/* Hero */}
      <div className="bg-[#0a0f0d] border-b border-[#233129]">
        <div className="max-w-3xl mx-auto px-6 py-10 flex flex-wrap items-center gap-6">
          <img src={LOGO_URL} alt="Barmann Lawn Service" className="w-16 h-16 object-contain" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8ba394]">
              Firefighter-owned · Loganville, GA
            </div>
            <h1 className="font-display text-3xl font-black text-[#eae6db] tracking-tight">Barmann Lawn Service</h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8ba394]">Invoice</div>
            <div className="font-mono font-bold text-[#e56b42] text-xl">#{inv.number}</div>
            <div className="text-xs text-[#8ba394] mt-1">Issued {inv.issued_date}</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-8 space-y-6">
        {/* Status / Pay card */}
        <div className="bm-invoice-paper p-8" data-testid="invoice-amount-card">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-60 mb-1">Amount Due</div>
              <div className={`font-display text-5xl font-black ${paid ? "text-[#1E3B2D]" : "text-[#C83E2D]"}`}>
                ${Number(inv.total).toFixed(2)}
              </div>
              <div className="text-sm mt-2 opacity-70">
                For {inv.customer.name}
                {inv.customer.address ? ` · ${inv.customer.address}` : ""}
              </div>
            </div>
            {paid ? (
              <div className="flex items-center gap-2 text-[#1E3B2D] border-2 border-[#1E3B2D] px-5 py-3 rounded-sm" data-testid="paid-badge">
                <CheckCircle2 className="w-6 h-6" />
                <span className="font-display font-black text-xl uppercase">Paid</span>
              </div>
            ) : inv.pay_enabled ? (
              <button
                onClick={payNow}
                disabled={paying || polling === "checking"}
                className="bm-btn bm-btn-primary text-base px-7 py-4"
                data-testid="pay-now-button"
              >
                {paying || polling === "checking" ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {polling === "checking" ? "Verifying…" : "Redirecting…"}</>
                ) : (
                  <><CreditCard className="w-5 h-5" /> Pay ${Number(inv.total).toFixed(2)}</>
                )}
              </button>
            ) : (
              <div className="text-xs text-[#6B7A70] max-w-[220px] text-right">
                Online payment unavailable. Please call <a className="underline" href="tel:6785596163">(678) 559-6163</a> to settle.
              </div>
            )}
          </div>
        </div>

        {/* Invoice details */}
        <div className="bm-invoice-paper p-8" data-testid="invoice-details-card">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-60 mb-4">Details</div>
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#0a0f0d]/20">
                <th className="text-left text-[10px] uppercase tracking-widest font-bold pb-2">Description</th>
                <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-14">Qty</th>
                <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-24">Price</th>
                <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items.map((li, idx) => (
                <tr key={idx} className="border-b border-[#0a0f0d]/10">
                  <td className="py-3 text-sm">{li.description}</td>
                  <td className="text-right font-mono py-3 text-sm">{li.quantity}</td>
                  <td className="text-right font-mono py-3 text-sm">${Number(li.unit_price).toFixed(2)}</td>
                  <td className="text-right font-mono py-3 text-sm font-bold">
                    ${(Number(li.quantity) * Number(li.unit_price)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mt-4">
            <div className="w-56 space-y-1 text-sm">
              <div className="flex justify-between font-display font-black text-lg border-t-2 border-[#0a0f0d] pt-2 mt-1">
                <span>Total</span><span className="font-mono text-[#C83E2D]">${Number(inv.total).toFixed(2)}</span>
              </div>
            </div>
          </div>
          {inv.notes && (
            <div className="mt-6 p-4 bg-[#0a0f0d]/5 border-l-4 border-[#C83E2D]">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-60">Notes</div>
              <div className="text-sm whitespace-pre-line">{inv.notes}</div>
            </div>
          )}
        </div>

        {/* Proof of Service photos */}
        {inv.photos.length > 0 && (
          <div className="bm-invoice-paper p-8" data-testid="proof-of-service-card">
            <div className="flex items-center gap-2 mb-5">
              <Camera className="w-5 h-5 text-[#C83E2D]" />
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-60">Proof of Service</div>
            </div>
            {jobKeys.map((k) => {
              const group = photosByJob[k];
              const dateLabel = (() => {
                try { return format(parseISO(group.label), "MMM d, yyyy"); } catch { return group.label; }
              })();
              return (
                <div key={k} className="mb-6 last:mb-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-3.5 h-3.5 opacity-60" />
                    <span className="font-display font-bold">{dateLabel}</span>
                    <span className="text-xs opacity-60">· {group.service}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {group.items.map((p) => <PhotoTile key={p.id} token={token} photo={p} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer / contact */}
        <div className="text-center text-[#8ba394] text-xs pt-6 space-y-2" data-testid="portal-footer">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <a href="tel:6785596163" className="inline-flex items-center gap-1.5 hover:text-[#eae6db]">
              <Phone className="w-3.5 h-3.5" /> (678) 559-6163
            </a>
            <a href="mailto:barmannlawnservices@yahoo.com" className="inline-flex items-center gap-1.5 hover:text-[#eae6db]">
              <Mail className="w-3.5 h-3.5" /> barmannlawnservices@yahoo.com
            </a>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {inv.company.service_area}
            </span>
          </div>
          <div>Owned & operated by a dedicated Firefighter and Paramedic.</div>
        </div>
      </div>
    </div>
  );
}
